// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: addresses for the Agents browser (design/agents-browser, A): what someone types into the
// address bar, how the bar shows a page's address, the links a chat holds for the quick picks, and
// the host:port a page goes to (matched against what the machine refused).

import type {AgentDevServer} from '@konvo/schema/src/domains/konvo/AgentBrowser';

/** Names that mean a machine's own or local network: typed without a scheme, they get http. */
const LOCAL_HOST =
	/^(localhost|[^/]*\.localhost|\[[0-9a-f:.]+\]|\d{1,3}(\.\d{1,3}){3}|[^/.]+|[^/]*\.(local|test|internal|lan|home\.arpa))(:\d+)?$/i;

/**
 * The page to open for what someone typed: a full http(s) address as is; "5173" or ":5173" as
 * localhost:5173; a local name or an IP over http; any other name over https. Null for anything that
 * is not an address (spaces, other schemes).
 */
export function normalizeAddress(input: string): string | null {
	const text = input.trim();
	if (!text || /\s/.test(text)) return null;
	let candidate: string;
	if (/^\d{1,5}$/.test(text)) candidate = `http://localhost:${text}/`;
	else if (/^:\d{1,5}(\/.*)?$/.test(text)) candidate = `http://localhost${text}`;
	else if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) candidate = text;
	else if (text.startsWith('//')) candidate = `http:${text}`;
	else {
		const host = text.split(/[/?#]/)[0] ?? '';
		candidate = `${LOCAL_HOST.test(host) ? 'http' : 'https'}://${text}`;
	}
	try {
		const url = new URL(candidate);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
		if (!url.hostname) return null;
		return url.toString();
	} catch {
		return null;
	}
}

/** The address bar at rest: the host bold, the rest quiet ("localhost:5173" + "/emails"). */
export function addressParts(url: string): {host: string; rest: string; secure: boolean} {
	try {
		const parsed = new URL(url);
		const rest = `${parsed.pathname === '/' ? '' : parsed.pathname}${parsed.search}${parsed.hash}`;
		return {host: parsed.host, rest, secure: parsed.protocol === 'https:'};
	} catch {
		return {host: url, rest: '', secure: false};
	}
}

/** Where a page goes, as the tunnel names it: host (no brackets, lower case) and port. */
export function targetOf(url: string): {host: string; port: number} | null {
	try {
		const parsed = new URL(url);
		if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
		const port = parsed.port ? Number(parsed.port) : parsed.protocol === 'https:' ? 443 : 80;
		return {host: parsed.hostname.replace(/^\[(.*)\]$/, '$1').toLowerCase(), port};
	} catch {
		return null;
	}
}

export const targetKey = (target: {host: string; port: number}) => `${target.host.toLowerCase()}:${target.port}`;

/** Pages that need the person's own sign-in: they open in their own browser, not through the machine. */
export function opensOutside(url: string): boolean {
	try {
		const host = new URL(url).hostname.toLowerCase();
		return host === 'github.com' || host.endsWith('.github.com');
	} catch {
		return false;
	}
}

export interface ChatLink {
	url: string;
	source: 'reply' | 'prompt';
	at: string;
}

const URL_IN_TEXT =
	/\bhttps?:\/\/[^\s<>()[\]{}"'`]+|\b(?:localhost|127\.0\.0\.1)(?::\d{1,5})(?:\/[^\s<>()[\]{}"'`]*)?/gi;

/** The web addresses a chat names, newest first, each once (the address bar's "Linked in this chat"). */
export function linksInThread(
	items: ReadonlyArray<{kind: string; text?: string; createdAt?: string}>,
	max = 8,
): Array<ChatLink> {
	const seen = new Set<string>();
	const out: Array<ChatLink> = [];
	for (let i = items.length - 1; i >= 0 && out.length < max; i--) {
		const item = items[i]!;
		if ((item.kind !== 'assistant' && item.kind !== 'user') || !item.text) continue;
		const found = item.text.match(URL_IN_TEXT) ?? [];
		for (const raw of found) {
			const trimmed = raw.replace(/[.,;:!?*_~]+$/, '');
			const url = normalizeAddress(trimmed);
			if (!url || seen.has(url)) continue;
			seen.add(url);
			out.push({url, source: item.kind === 'assistant' ? 'reply' : 'prompt', at: item.createdAt ?? ''});
			if (out.length >= max) break;
		}
	}
	return out;
}

/** The machine's dev servers for the picks: this chat's own first, then by port. */
export function orderServers(servers: ReadonlyArray<AgentDevServer>, threadId: string | null): Array<AgentDevServer> {
	return [...servers].sort((a, b) => {
		const own = Number(b.threadId === threadId && !!threadId) - Number(a.threadId === threadId && !!threadId);
		return own || a.port - b.port;
	});
}

/** Whether a pick matches what is typed (host, port, path or title). */
export function pickMatches(query: string, ...fields: Array<string | undefined>): boolean {
	const q = query
		.trim()
		.toLowerCase()
		.replace(/^https?:\/\//, '');
	if (!q) return true;
	return fields.some((field) =>
		field
			?.toLowerCase()
			.replace(/^https?:\/\//, '')
			.includes(q),
	);
}

/** "390 wide" at phone width; desktop renders this wide and scales to fit. */
export const PHONE_WIDTH = 390;
export const DESKTOP_WIDTH = 1280;

/** How much a page this wide has to shrink to fit (never grows). */
export function fitScale(pageWidth: number, room: number): number {
	if (room <= 0) return 1;
	return Math.min(1, room / pageWidth);
}
